import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { OcmPoi } from '../../../stores/types/ocm.interface';
import { ColorType, Label, LabelType } from '../../_ui';
import { OutboundLink } from './OutboundLink.component';
import { getNetworkName } from './utils/networks';
import {
  ContactDetails,
  getPaymentRequirementKeys,
  getPhoneHref,
  getPoiContacts,
  getUrlHost,
  hasContactDetails,
} from './utils/poiDisplay';

interface NearbyChargerSiteDetailsProps {
  poi: OcmPoi;
}

const InfoRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <>
    <Label text={label} type={LabelType.LABEL_XS} color={ColorType.GREY5} className='pt-0.5' />
    <div className='flex flex-col min-w-0 text-xs font-inter text-grey6 break-words'>
      {children}
    </div>
  </>
);

const ContactLinks = ({ contact }: { contact: ContactDetails }) => {
  const { t } = useTranslation();
  return (
    <>
      {contact.phones.map((phone) => (
        <OutboundLink key={phone} href={getPhoneHref(phone)} openInNewTab={false}>
          {phone}
        </OutboundLink>
      ))}
      {contact.email && (
        <OutboundLink href={`mailto:${contact.email}`} openInNewTab={false}>
          {contact.email}
        </OutboundLink>
      )}
      {contact.websiteUrl && (
        <OutboundLink href={contact.websiteUrl}>{getUrlHost(contact.websiteUrl)}</OutboundLink>
      )}
      {contact.bookingUrl && (
        <OutboundLink href={contact.bookingUrl}>{t('nearby_chargers_book_online')}</OutboundLink>
      )}
    </>
  );
};

/** Network, pricing, contact and access details. OCM data is crowd-sourced, so most are optional */
export const NearbyChargerSiteDetails = ({ poi }: NearbyChargerSiteDetailsProps) => {
  const { t } = useTranslation();
  const networkName = getNetworkName(poi.OperatorInfo);
  const contacts = getPoiContacts(poi);
  const paymentRequirements = getPaymentRequirementKeys(poi.UsageType).map((key) => t(key));
  const notListed = <span className='italic text-grey5'>{t('nearby_chargers_not_listed')}</span>;
  const chargingPoints = poi.NumberOfPoints
    ? t(
        poi.NumberOfPoints === 1
          ? 'nearby_chargers_charging_point'
          : 'nearby_chargers_charging_points',
        { total: poi.NumberOfPoints },
      )
    : null;

  const renderNetwork = () => {
    if (!networkName) return notListed;
    const { websiteUrl } = contacts.network;
    return websiteUrl ? (
      <OutboundLink href={websiteUrl}>{networkName}</OutboundLink>
    ) : (
      <span>{networkName}</span>
    );
  };

  return (
    <div className='grid grid-cols-[64px_1fr] gap-x-2 gap-y-1'>
      <InfoRow label={t('nearby_chargers_network')}>{renderNetwork()}</InfoRow>
      <InfoRow label={t('nearby_chargers_pricing')}>
        {poi.UsageCost?.trim() ? <span>{poi.UsageCost}</span> : notListed}
        {paymentRequirements.length > 0 && (
          <span className='text-grey5'>{paymentRequirements.join(', ')}</span>
        )}
      </InfoRow>
      <InfoRow label={t('nearby_chargers_contact')}>
        {hasContactDetails(contacts.network) ? (
          <ContactLinks contact={contacts.network} />
        ) : (
          notListed
        )}
      </InfoRow>
      {hasContactDetails(contacts.site) && (
        <InfoRow label={t('nearby_chargers_site')}>
          <ContactLinks contact={contacts.site} />
        </InfoRow>
      )}
      <InfoRow label={t('nearby_chargers_access')}>
        <span>
          {[poi.UsageType?.Title || t('nearby_chargers_unknown'), chargingPoints]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </InfoRow>
    </div>
  );
};
